import { useEffect } from "react";
import { Plus, Trash2 } from "lucide-react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import StyledSwitch from "@/components/ui/customs/StyledSwitch";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";
import { ExpeditionBoDeliveryInfoFormData } from "@/types/forms/bo/expeditionBo.form";

export const DaeNumbers = () => {
  const { t } = useTranslation("boExpeditionForm");
  const { control, setValue } = useFormContext<ExpeditionBoDeliveryInfoFormData>();

  const items = useWatch({ control, name: "options.daeNumbers" }) ?? [];
  const daeEnabled = useWatch({ control, name: "options.dae" });

  useEffect(() => {
    if (!daeEnabled) {
      if (items.length > 0) {
        setValue("options.daeNumbers", [], { shouldDirty: true });
      }
      return;
    }
    if (items.length === 0) {
      setValue("options.daeNumbers", [""], { shouldDirty: true });
    }
  }, [daeEnabled, items.length, setValue]);

  const append = () => setValue("options.daeNumbers", [...items, ""], { shouldDirty: true });

  const remove = (index: number) =>
    setValue(
      "options.daeNumbers",
      items.filter((_, i) => i !== index),
      { shouldDirty: true },
    );

  const visibleItems = daeEnabled ? (items.length === 0 ? [""] : items) : [];
  const canDelete = visibleItems.length > 1;

  return (
    <div className="flex w-full flex-col gap-2 @xl:flex-row @xl:items-start @xl:gap-2">
      <div className="flex flex-1 flex-row items-center gap-2 @xl:h-9">
        <StyledSwitch control={control} name="options.dae" />
        <span className="w-24 shrink-0 text-sm font-medium text-gray-800">DAE</span>
      </div>
      <div className="flex w-full flex-col gap-2">
        {daeEnabled &&
          visibleItems.map((_item, index) => (
            <div key={index} className="flex flex-row items-center gap-2">
              <ControlledUnifiedInput
                control={control}
                name={`options.daeNumbers.${index}`}
                placeholder={t("daeNumbers.numberPlaceholder")}
                disabled={!daeEnabled}
                wrapperClassName="flex-1"
              />
              {canDelete && (
                <Button
                  variant="outline"
                  type="button"
                  disabled={!daeEnabled}
                  onClick={() => remove(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        {daeEnabled && (
          <Button
            className="w-fit"
            variant="outline"
            type="button"
            disabled={!daeEnabled}
            onClick={() => append()}
          >
            <Plus className="h-4 w-4" />
            {t("daeNumbers.addNumber")}
          </Button>
        )}
      </div>
    </div>
  );
};
